import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { resetPassword } from '../../modules/platform/api/auth';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { validatePassword } from '../../utils/validation';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const password = watch('password');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      showErrorToast(new Error('Reset token is missing'));
      return;
    }

    if (data.password !== data.confirmPassword) {
      showErrorToast(new Error('Passwords do not match'));
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({ token, password: data.password });
      showSuccessToast('Password reset successful!');
      navigate(ROUTES.LOGIN);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-error-600 mb-4">
              Invalid reset link. Please request a new password reset.
            </p>
            <Link to={ROUTES.FORGOT_PASSWORD}>
              <Button className="w-full">Request New Reset Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Reset Password</CardTitle>
          <p className="text-center text-gray-600 dark:text-gray-400 mt-2">
            Enter your new password
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="New Password"
              type="password"
              {...register('password', {
                required: 'Password is required',
                validate: (value) => {
                  const result = validatePassword(value);
                  return result.valid || result.message;
                },
              })}
              error={errors.password?.message}
              placeholder="Create a strong password"
              helperText="Must be at least 8 characters with uppercase, lowercase, and number"
            />

            <Input
              label="Confirm Password"
              type="password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match',
              })}
              error={errors.confirmPassword?.message}
              placeholder="Confirm your password"
            />

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Reset Password
            </Button>

            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              <Link
                to={ROUTES.LOGIN}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

